import type React from 'react';
import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Share2, Check } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '@showliner/shared/components/Logo';
import { Button } from '@/components/ui/button';

const ProfileLayout: React.FC = () => {
  const { pathname } = useLocation();
  const [justCopied, setJustCopied] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger — scroll to top on every navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const handleShare = async () => {
    const url = window.location.href;
    const title = document.title || 'Showliner';

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setJustCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setJustCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleCookiePreferences = () => {
    window.dispatchEvent(new CustomEvent('showliner:open-cookie-prefs'));
    toast.info('Cookie preferences coming soon');
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-ink focus:text-grey-50 focus:px-4 focus:py-2 focus:text-sm"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-50 w-full">
        <div className="mx-auto flex h-14 max-w-lg items-center justify-between px-4">
          <Link to="/" aria-label="Showliner home" className="flex items-center">
            <Logo variant="icon" theme="light" className="h-7 w-auto" />
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShare}
            aria-label="Share this profile"
          >
            {justCopied ? (
              <>
                <Check className="mr-1 h-4 w-4" />
                Copied
              </>
            ) : (
              <>
                <Share2 className="mr-1 h-4 w-4" />
                Share
              </>
            )}
          </Button>
        </div>
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-border/40 mt-12">
        <div className="mx-auto max-w-lg px-4 py-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">
            What is Showliner?
          </Link>
          <span aria-hidden="true">·</span>
          <Link to="/privacy" className="hover:text-foreground transition-colors">
            Privacy
          </Link>
          <span aria-hidden="true">·</span>
          <button
            type="button"
            onClick={handleCookiePreferences}
            className="hover:text-foreground transition-colors"
          >
            Cookie preferences
          </button>
        </div>
      </footer>
    </div>
  );
};

export default ProfileLayout;
